import React from 'react';
import { TrendingUp, Shield, Calculator, Target, DollarSign, BarChart3, CheckCircle, ArrowRight, Building2, MessageCircle, Star, PieChart } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const InvestidoresPage = () => {
  const features = [
    {
      icon: <Calculator className="h-8 w-8 text-purple-600" />,
      title: "Análise de ROI Completa",
      description: "Calcule o retorno real considerando todos os custos: reforma, impostos, taxas e potencial de valorização.",
      benefits: ["Cálculo de 50+ variáveis", "Projeções de 5 anos", "Cenários otimista/pessimista"]
    },
    {
      icon: <Shield className="h-8 w-8 text-purple-600" />,
      title: "Identificação de Riscos",
      description: "Nossa IA identifica automaticamente ônus, penhoras, ocupações e problemas documentais.",
      benefits: ["Análise de matrícula", "Verificação de débitos", "Histórico do imóvel"]
    },
    {
      icon: <Target className="h-8 w-8 text-purple-600" />,
      title: "Oportunidades Filtradas",
      description: "Receba apenas as melhores oportunidades baseadas no seu perfil e critérios de investimento.",
      benefits: ["Filtros personalizados", "Alertas automáticos", "Score de oportunidade"]
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-purple-600" />,
      title: "Análise de Mercado",
      description: "Dados de valorização histórica, tendências e projeções futuras para cada região.",
      benefits: ["Dados históricos", "Tendências de mercado", "Comparativos regionais"]
    }
  ];

  const strategies = [
    {
      title: "Buy & Hold",
      description: "Imóveis para renda passiva com aluguel",
      roi: "12-18% a.a.",
      risk: "Baixo",
      timeframe: "Longo prazo",
      investment: "R$ 50k - R$ 300k"
    },
    {
      title: "Fix & Flip",
      description: "Reforma e revenda rápida",
      roi: "25-40%",
      risk: "Médio",
      timeframe: "6-12 meses",
      investment: "R$ 80k - R$ 500k"
    },
    {
      title: "Desenvolvimento",
      description: "Terrenos para construção",
      roi: "40-80%",
      risk: "Alto",
      timeframe: "2-3 anos",
      investment: "R$ 200k - R$ 2M"
    }
  ];

  const testimonials = [
    {
      name: "Eduardo Mendes",
      role: "Investidor - Portfolio R$ 2.3M",
      content: "Com o ImóvelIA, identifiquei oportunidades que me geraram R$ 680k de lucro em 18 meses.",
      rating: 5,
      image: "https://images.pexels.com/photos/614810/pexels-photo-614810.jpeg"
    },
    {
      name: "Patricia Oliveira",
      role: "Investidora - 12 imóveis",
      content: "A análise de riscos me salvou de 3 péssimos investimentos. O ROI das ferramentas é incrível.",
      rating: 5,
      image: "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg"
    }
  ];

  const portfolioExamples = [
    {
      type: "Conservador",
      allocation: "70% Buy & Hold, 30% Fundos",
      expectedReturn: "14% a.a.",
      risk: "Baixo"
    },
    {
      type: "Moderado",
      allocation: "50% Buy & Hold, 30% Fix & Flip, 20% Fundos",
      expectedReturn: "22% a.a.",
      risk: "Médio"
    },
    {
      type: "Agressivo",
      allocation: "30% Buy & Hold, 40% Fix & Flip, 30% Desenvolvimento",
      expectedReturn: "35% a.a.",
      risk: "Alto"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-r from-purple-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-purple-100 font-medium mb-6">
              <TrendingUp className="h-5 w-5" />
              <span>Especializado para Investidores</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Maximize seus Retornos com Inteligência Artificial
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 mb-8">
              Encontre as melhores oportunidades de investimento em leilões com análises precisas e dados confiáveis
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/5516999631188?text=Olá! Sou investidor e quero maximizar meus retornos com o ImóvelIA"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-purple-600 hover:bg-purple-50 px-8 py-4 rounded-lg font-medium transition-colors text-lg"
              >
                <MessageCircle className="h-5 w-5" />
                Falar com Especialista
              </a>
              <a
                href="https://pay.kiwify.com.br/cSvs3ux"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-400 text-white px-8 py-4 rounded-lg font-medium transition-colors text-lg"
              >
                <TrendingUp className="h-5 w-5" />
                Começar Análise
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Strategies */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Estratégias de Investimento Comprovadas
            </h2>
            <p className="text-xl text-slate-600">
              Escolha a estratégia que melhor se adapta ao seu perfil e objetivos
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {strategies.map((strategy, index) => (
              <div key={index} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">{strategy.title}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    strategy.risk === 'Baixo' ? 'bg-green-100 text-green-700' :
                    strategy.risk === 'Médio' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {strategy.risk}
                  </span>
                </div>
                <p className="text-slate-600 mb-4">{strategy.description}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">ROI:</span>
                    <span className="font-semibold text-purple-600">{strategy.roi}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Prazo:</span>
                    <span className="font-medium">{strategy.timeframe}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Investimento:</span>
                    <span className="font-medium">{strategy.investment}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-purple-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-center mb-8">Exemplos de Portfolio</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {portfolioExamples.map((portfolio, index) => (
                <div key={index} className="bg-white p-6 rounded-lg">
                  <h4 className="font-semibold text-lg mb-2">{portfolio.type}</h4>
                  <p className="text-slate-600 text-sm mb-4">{portfolio.allocation}</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-purple-600 font-medium">Retorno: {portfolio.expectedReturn}</span>
                    <span className="text-slate-500">Risco: {portfolio.risk}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Análise Profissional de Investimentos
            </h2>
            <p className="text-xl text-slate-600">
              Ferramentas avançadas para tomar decisões inteligentes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-start gap-4 mb-6">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {feature.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-purple-500" />
                      <span className="text-sm text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Resultados dos Nossos Investidores
            </h2>
            <p className="text-xl text-slate-600">
              Veja o impacto real do ImóvelIA nos investimentos
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16">
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">R$ 2.3M</div>
              <div className="text-slate-600">Volume investido</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">28%</div>
              <div className="text-slate-600">ROI médio anual</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">156</div>
              <div className="text-slate-600">Imóveis analisados</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">89%</div>
              <div className="text-slate-600">Taxa de sucesso</div>
            </div>
          </div>

          {/* Testimonials */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-purple-50 rounded-xl p-6">
                <div className="flex items-center gap-4 mb-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-semibold">{testimonial.name}</h4>
                    <p className="text-sm text-slate-600">{testimonial.role}</p>
                  </div>
                  <div className="ml-auto flex">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                </div>
                <p className="text-slate-700 italic">"{testimonial.content}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Pronto para Investir com Inteligência?
          </h2>
          <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
            Junte-se aos investidores que já descobriram o poder da análise automatizada para maximizar retornos
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/5516999631188?text=Olá! Quero uma consultoria personalizada para investimentos em leilões"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-purple-600 hover:bg-purple-50 px-8 py-4 rounded-lg font-medium transition-colors"
            >
              <Target className="h-5 w-5" />
              Consultoria Personalizada
              <ArrowRight className="h-5 w-5" />
            </a>
            <a
              href="https://pay.kiwify.com.br/cSvs3ux"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-400 text-white px-8 py-4 rounded-lg font-medium transition-colors"
            >
              <Calculator className="h-5 w-5" />
              Começar Agora - R$ 79,90/mês
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InvestidoresPage;